<script lang="ts" setup>
import { computed, ImgHTMLAttributes } from 'vue'
import { MediaImageFragment } from '~/api/graphql/generated'
import { buildResponsiveImageSlices } from '~/lib/utils'
import type { ResponsiveImageSlice } from '~/lib/utils'

type Size = [number | null, string][]

const imageSizes = {
  teaser: [
    [768, '100vw'],
    [1024, '50vw'],
    [null, '520px'],
  ] as Size,
  'teaser-full': [[null, '100vw']] as Size,
  'teaser-main-menu': [[null, '200px']] as Size,
  employee: [
    [1024, '300px'],
    [null, '400px'],
  ] as Size,
  'employee-teaser': [[null, '300px']] as Size,
  'teaser-single': [
    [1024, '100vw'],
    [1440, '50vw'],
    [null, '1100px'],
  ] as Size,
  'author-thumbnail': [[null, '120px']] as Size,
  'sidebar-logo': [[null, '150px']] as Size,
  'room-overview': [
    [1024, '100vw'],
    [null, '660px'],
  ] as Size,
  'seating-plan': [
    [1024, '100vw'],
    [null, '580px'],
  ] as Size,
  'image-type-default': [
    [null, '100vw'],
    [1440, '50vw'],
    [null, '1230px'],
  ] as Size,
  'image-type-small': [
    [null, '100vw'],
    [1440, '50vw'],
    [null, '830px'],
  ] as Size,
  'image-type-medium': [
    [1440, '100vw'],
    [null, '1440px'],
  ] as Size,
  'image-type-large': [[null, '100vw']] as Size,
}

export type ImageSizes = keyof typeof imageSizes

type FetchPriority = 'high' | 'low' | 'auto'

const getSizes = (type: ImageSizes) =>
  imageSizes[type]
    .reduce((acc, item) => {
      if (!item[0]) return `${acc}${item[1]}, `
      return `${acc}(max-width: ${item[0] - 1}px) ${item[1]}, `
    }, '')
    .slice(0, -2)

const getSrcset = (data: ResponsiveImageSlice[]) =>
  data
    .reduce((acc: string, slice: ResponsiveImageSlice): string => `${acc}${slice.url} ${slice.width}w,`, '')
    .slice(0, -1)

const props = withDefaults(
  defineProps<{
    entity: MediaImageFragment
    type?: ImageSizes
    loading?: 'lazy' | 'eager'
    fetchpriority?: FetchPriority
  }>(),
  {
    type: 'teaser',
    loading: 'lazy',
    fetchpriority: 'auto',
  },
)

const emit = defineEmits<{
  load: [event: Event]
  error: [event: Event]
}>()

type ImageData = MediaImageFragment['fieldMediaImage']

const getAttrs = (imageData: ImageData | undefined) => {
  if (!imageData) return undefined

  const slices = buildResponsiveImageSlices(imageData)

  const primarySlice = slices[0]
  if (!primarySlice) return undefined
  const width = imageData.width ?? primarySlice.width
  const height = imageData.height ?? primarySlice.height

  const attrs = {
    class: 'dp-image',
    width,
    height,
    src: primarySlice.url,
    srcset: getSrcset(slices),
    sizes: getSizes(props.type),
    alt: imageData?.alt || '',
    loading: props.loading,
  } as ImgHTMLAttributes & { fetchpriority?: FetchPriority }

  if (props.fetchpriority && props.fetchpriority !== 'auto') {
    attrs.fetchpriority = props.fetchpriority
  }

  return attrs
}

const attrs = computed(() => getAttrs(props.entity?.fieldMediaImage))

const imgRef = ref<HTMLImageElement>()

const onLoad = (event: Event) => {
  emit('load', event)
}

const onError = (event: Event) => {
  emit('error', event)
}

// Check if image is already loaded (for cached images on page reload)
onMounted(() => {
  if (imgRef.value && imgRef.value.complete && imgRef.value.naturalHeight !== 0) {
    emit('load', new Event('load'))
  }
})
</script>
<template>
  <img v-if="attrs" ref="imgRef" v-bind="attrs as ImgHTMLAttributes" @load="onLoad" @error="onError" />
</template>

<style scoped lang="scss">
img {
  object-fit: cover;
}
</style>
